import { ApolloError, DefaultContext, useMutation } from '@apollo/client'
import { Alert, NumberInput } from '@designsforhealth/dfh-react-components'
import React, { FC, useContext, useEffect, useMemo } from 'react'
import { FieldErrors, RegisterOptions, useForm } from 'react-hook-form'
import styled from 'styled-components'

import {
  RequestRebatesPayoutDocument,
  RequestRebatesPayoutMutation,
} from '../../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../../hooks/useLogAndCaptureError'
import { ApolloClientsContext } from '../../../../../../lib/graphql/apollo'
import {
  getRebateBalanceAmounts,
  getRedeemForCashAmounts,
} from '../../../../../../lib/rewards/rebates'
import {
  DetailsPayoutModal,
  DetailsPayoutModalActions,
  DetailsPayoutModalAlerts,
  DetailsPayoutModalHeading,
  DetailsPayoutModalPrimaryButton,
  DetailsPayoutModalSecondaryButtonLink,
  DetailsPayoutModalText,
} from '../DetailsPayoutModal'

export interface RebatesRequestPayoutModalProps {
  className?: string
  rebateBalance: number
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface RebateRequestPayoutFormFieldValues {
  balanceToRedeem: number
}

const getNumberInputValidations = ({
  balanceAmounts,
}: {
  balanceAmounts: ReturnType<typeof getRebateBalanceAmounts>
}): Omit<RegisterOptions, 'required'> => {
  return {
    valueAsNumber: true,
    min: {
      message: `${balanceAmounts.minRedeemable.formatted} is the minimum redeemable Rebate.`,
      value: balanceAmounts.minRedeemable.value,
    },
    max: {
      message: `${balanceAmounts.maxRedeemable.formatted} is the maximum redeemable Rebate.`,
      value: balanceAmounts.maxRedeemable.value,
    },
  }
}

const parseFormErrors = ({
  formErrors,
}: {
  formErrors: FieldErrors<RebateRequestPayoutFormFieldValues>
}) => {
  let errorMessage = null
  if (formErrors?.balanceToRedeem) {
    if (formErrors.balanceToRedeem.message) {
      errorMessage = formErrors.balanceToRedeem.message
    } else {
      errorMessage = 'Please enter a valid amount.'
    }
  }
  return errorMessage
}

const parseMutationData = ({ data }: { data?: RequestRebatesPayoutMutation | null }) => {
  if (data && data.requestRebatesPayout) {
    return data.requestRebatesPayout
  }
  return null
}

const parseMutationError = ({ error }: { error?: ApolloError | Error }) => {
  if (error) {
    return 'Cannot request payout at this time, please try again later.'
  }
  return null
}

const StyledErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.semantic.error};
`

export const RebatesRequestPayoutModal: FC<RebatesRequestPayoutModalProps> = ({
  className,
  rebateBalance,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const {
    handleSubmit,
    formState,
    errors: formErrors,
    register,
    watch,
  } = useForm<RebateRequestPayoutFormFieldValues>({
    mode: 'onChange',
  })

  const balanceAmounts = useMemo(() => getRebateBalanceAmounts({ rebateBalance }), [rebateBalance])
  const balanceToRedeem = watch('consumeAmountMax', balanceAmounts.balance.value)
  const redeemAmounts = useMemo(
    () => getRedeemForCashAmounts({ balanceToRedeem }),
    [balanceToRedeem],
  )

  const { comma: commaClient } = useContext(ApolloClientsContext)
  const [requestRebatesPayout, { data, loading, called, error }] = useMutation(
    RequestRebatesPayoutDocument,
    {
      client: commaClient,
      context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
      update: (cache, result) => {
        if (result.data?.requestRebatesPayout?.success) {
          cache.modify({
            fields: {
              customer: (prev) => ({
                ...prev,
                availableBalance: {
                  ...prev.availableBalance,
                  rebateBalance: {
                    ...prev.availableBalance.rebateBalance,
                    amount: (
                      parseFloat(prev.availableBalance.rebateBalance.amount) - balanceToRedeem
                    ).toFixed(2),
                  },
                  totalBalance: {
                    ...prev.availableBalance.totalBalance,
                    amount: (
                      parseFloat(prev.availableBalance.totalBalance.amount) - balanceToRedeem
                    ).toFixed(2),
                  },
                },
              }),
            },
          })
        }
      },
    },
  )
  useLogAndCaptureError(error)

  const response = parseMutationData({ data })
  const formErrorMessage = parseFormErrors({ formErrors })
  const mutationErrorMessage = parseMutationError({ error })
  const isSuccess = !!(called && !loading && response)
  const disableSubmit = !formState.isValid || loading || isSuccess

  const submitRequest = () => {
    if (!disableSubmit) {
      return requestRebatesPayout({
        variables: {
          input: {
            consumeAmountMax: balanceToRedeem.toString(),
          },
        },
      })
    }
    return undefined
  }

  const closeModal = () => onClose()

  useEffect(() => {
    if (isSuccess) {
      onSuccess()
    }
  }, [isSuccess, onSuccess])

  return (
    <DetailsPayoutModal className={className} isOpen={isOpen} onClose={closeModal}>
      <DetailsPayoutModalHeading>Rebates payout</DetailsPayoutModalHeading>
      <DetailsPayoutModalText>
        You have {balanceAmounts.balance.formatted} available in Rebates. This can be redeemed for{' '}
        {balanceAmounts.maxPayoutAmount.formatted} in cash.
      </DetailsPayoutModalText>
      <form onSubmit={handleSubmit(submitRequest)}>
        <h5>Please enter the amount of Rebate to request payout for:</h5>
        <NumberInput
          defaultValue={`${balanceAmounts.balance.value}`}
          errors={formErrors}
          id="consumeAmountMax"
          label="Amount to redeem"
          name="consumeAmountMax"
          register={register}
          required
          validations={getNumberInputValidations({ balanceAmounts })}
          step={0.01}
        />

        <p className="small-paragraph">
          This will result in a payout of <strong>{redeemAmounts.payout.formatted}</strong>
        </p>

        {formErrorMessage && (
          <DetailsPayoutModalAlerts>
            <StyledErrorMessage className="small-paragraph error-text">
              {formErrorMessage}
            </StyledErrorMessage>
          </DetailsPayoutModalAlerts>
        )}
        {isSuccess && (
          <DetailsPayoutModalAlerts>
            <Alert type="success">Redemption request submitted successfully.</Alert>
          </DetailsPayoutModalAlerts>
        )}
        {mutationErrorMessage && (
          <DetailsPayoutModalAlerts>
            <Alert type="error">{mutationErrorMessage}</Alert>
          </DetailsPayoutModalAlerts>
        )}
        <DetailsPayoutModalActions>
          <DetailsPayoutModalPrimaryButton disabled={disableSubmit} type="submit">
            {loading ? 'Processing...' : isSuccess ? 'Success' : 'Submit Payout Request'}
          </DetailsPayoutModalPrimaryButton>
          <DetailsPayoutModalSecondaryButtonLink to="/products">
            Shop With Rebates Instead
          </DetailsPayoutModalSecondaryButtonLink>
        </DetailsPayoutModalActions>
      </form>
    </DetailsPayoutModal>
  )
}
